"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const TRANSITIONS = [
  { from: "Sent", to: "Accepted, Rejected" },
  { from: "Accepted", to: "Completed — only by saving a Sales Order from it" },
  { from: "Rejected", to: "None. Rejecting cannot be undone" },
  { from: "Completed", to: "None" },
];

const RULES = [
  {
    topic: "Creation",
    rule: "Not possible in Books. Orders arrive from the Customer Order portal after the customer has Enable Portal turned on, and only if that add-on is on your plan",
  },
  {
    topic: "No ledger posting",
    rule: "A Customer Order never posts to the general ledger. Only the Sales Order created from it can",
  },
  {
    topic: "Drafts",
    rule: "Orders still sitting as Draft in the portal never appear in this list. You only see them once the customer has submitted (Sent)",
  },
  {
    topic: "Accept / Reject",
    rule: "Only a Sent order can be accepted or rejected. Rejecting requires a typed reason and cannot be undone — the order stays on the list as Rejected",
  },
  {
    topic: "Converting",
    rule: "Only an Accepted order, and only into a Sales Order. You must pick Division, Branch, and Warehouse first — the portal order does not carry those. The Customer Order becomes Completed when that Sales Order is saved, not when you click Convert",
  },
  {
    topic: "Editing / deleting / cancelling",
    rule: "None of these exist in Books. You cannot change items or amounts, delete an order, or mark it Cancelled from this screen",
  },
  {
    topic: "Notifications",
    rule: "Accepting, rejecting, and completing (via Sales Order) each notify the customer",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. A review queue, not a place to raise orders" },
      { heading: "2. How an order gets here" },
      { heading: "3. Finding submitted orders" },
      { heading: "4. Opening one" },
      { heading: "5. Accepting or rejecting" },
      { heading: "6. Converting to a Sales Order" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function CustomerOrdersPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Customer Orders
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone reviewing orders a customer submitted through the
            portal, before they become a Sales Order
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            This is a <B>review queue</B>. Customers place the order in the
            Customer Order portal; you see it here once they&apos;ve submitted
            it. There is no Add button. Your job is to{" "}
            <B>accept</B> or <B>reject</B> what they sent, then convert an
            accepted order into a <B>Sales Order</B> so it can move through
            the rest of Books.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. A review queue, not a place to raise orders
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Books does not create, edit, or delete Customer Orders. The
            screen has no Add, no line-item editor, and no delete. What you
            can do is read what arrived, change status, and convert.
          </p>
          <Callout type="note">
            A Customer Order does <B>not</B> post to the ledger. Nothing hits
            stock or Accounts Receivable until you convert it and save the
            Sales Order.
          </Callout>
          <Screenshot caption="Screenshot: the Customer Orders list with no Add button — only search, filters, and row actions." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. How an order gets here
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Three things have to be true before a customer can submit an
            order you&apos;ll see in this list.
          </p>
          <Definitions
            items={[
              {
                term: "The plan",
                desc: (
                  <>
                    Customer Order is a plan add-on. On the customer record,
                    Enable Portal is greyed out until that feature is on.
                  </>
                ),
              },
              {
                term: "Enable Portal",
                desc: (
                  <>
                    Turned on for that customer under Other Details. They get
                    an invite; until they accept it you can resend the invite
                    from the customer list.
                  </>
                ),
              },
              {
                term: "The customer submits",
                desc: (
                  <>
                    While they&apos;re still drafting in the portal, the order
                    is <B>Draft</B> and does not appear here. Once they send
                    it, it shows as <B>Sent</B> and you can review it.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Enable Portal on a customer's Other Details, and Resend Invite on a customer who hasn't accepted yet." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Finding submitted orders
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The list shows order number, date, customer, status, amounts, and
            who added or last updated it (the customer, if they placed it in
            the portal).
          </p>
          <Definitions
            items={[
              {
                term: "Search",
                desc: "Order number or customer name.",
              },
              {
                term: "Filters",
                desc: (
                  <>
                    <B>Date range</B> and <B>status</B> (Sent, Accepted,
                    Rejected, Completed, Cancelled). There is no customer,
                    division, or branch filter on this screen.
                  </>
                ),
              },
            ]}
          />
          <Callout type="note">
            <B>Cancelled</B> can appear in the status filter, but Books has
            no Mark As Cancelled. That status is not something you set from
            this screen.
          </Callout>
          <Screenshot caption="Screenshot: Customer Orders filters showing date range and status only." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Opening one
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The detail page is read-only on the order itself.
          </p>
          <Definitions
            items={[
              {
                term: "Basic Info",
                desc: (
                  <>
                    Line items, quantities, rates, GST, totals, and the
                    customer&apos;s shipping address.
                  </>
                ),
              },
              {
                term: "History",
                desc: (
                  <>
                    Status changes and the rejection reason, if any. You
                    cannot add comments from here.
                  </>
                ),
              },
              {
                term: "Download / Print",
                desc: "A PDF of the order, the same as other sales documents.",
              },
              {
                term: "View Customer",
                desc: "Opens that customer's record without leaving the order.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: Customer Order Details with Basic Info, History, Download, and Print." />

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Accepting or rejecting
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Only a <B>Sent</B> order can be accepted or rejected. Use{" "}
            <B>Mark Order As</B> on the detail page, or the row&apos;s More
            Actions on the list.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    What happens next
                  </th>
                </tr>
              </thead>
              <tbody>
                {TRANSITIONS.map((t, i) => (
                  <tr
                    key={t.from}
                    className={
                      i < TRANSITIONS.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[140px]">
                      {t.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {t.to}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Definitions
            items={[
              {
                term: "Accept",
                desc: (
                  <>
                    The order becomes <B>Accepted</B> and the customer is
                    notified. Convert to Sales Order becomes available.
                  </>
                ),
              },
              {
                term: "Reject",
                desc: (
                  <>
                    You must type a reason. That reason is recorded in
                    History. The warning on the dialog is accurate: this{" "}
                    <B>cannot be undone</B>. The order stays on the list as
                    Rejected — it is not deleted.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Mark Order As on a Sent customer order, showing Accepted and Rejected." />

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Converting to a Sales Order
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Only an <B>Accepted</B> order can be converted, and only into a{" "}
            <B>Sales Order</B> — not an Invoice or Delivery Challan.
          </p>
          <Definitions
            items={[
              {
                term: "Location first",
                desc: (
                  <>
                    A portal order has no Division, Branch, or Warehouse. The
                    convert dialog asks for all three before opening Add Sales
                    Order.
                  </>
                ),
              },
              {
                term: "What carries over",
                desc: (
                  <>
                    Customer, items, GST, additional charges, shipping
                    address, notes, and payment terms for the branch you
                    picked.
                  </>
                ),
              },
              {
                term: "When it completes",
                desc: (
                  <>
                    The Customer Order stays <B>Accepted</B> until you save
                    the Sales Order. Saving it moves the Customer Order to{" "}
                    <B>Completed</B> and notifies the customer. Closing Add
                    Sales Order without saving leaves the order Accepted, so
                    you can convert again.
                  </>
                ),
              },
            ]}
          />
          <Callout type="warning">
            Completed cannot be converted again. One Customer Order becomes
            one Sales Order.
          </Callout>
          <Screenshot caption="Screenshot: Convert to Sales Order dialog asking for Division, Branch, and Warehouse." />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[16px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/sales/customer-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Customers <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Where Enable Portal and the invite actually live
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/sales-orders"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Sales Orders <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              The only document an Accepted Customer Order can become
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
